/**
 * JsonView - Interactive JSON tree viewer
 * Self-contained TypeScript module with embedded styles and CSS-based iconography
 */

import { htmlEscape } from "./vanilla";

// ─── Types ────────────────────────────────────────────────────────────────────

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonArray;

interface JsonObject {
    [key: string]: JsonValue;
}

type JsonArray = JsonValue[];

type JsonDataType = "string" | "number" | "boolean" | "null" | "object" | "array";

export interface JsonNode {
    key: string | null;
    parent: JsonNode | null;
    value: JsonValue;
    isExpanded: boolean;
    type: JsonDataType | null;
    children: JsonNode[];
    el: HTMLElement | null;
    depth: number;
    dispose: (() => void) | null;
}

interface CreateNodeOptions {
    key?: string | null;
    parent?: JsonNode | null;
    value?: JsonValue;
    isExpanded?: boolean;
    type?: JsonDataType | null;
    children?: JsonNode[];
    el?: HTMLElement | null;
    depth?: number;
}

interface ExpandedTemplateParams {
    key: string | null;
    size: string | null;
    isExpanded: boolean;
}

interface NotExpandedTemplateParams {
    key: string | null;
    value: string;
    type: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CSS_CLASS = {
    HIDDEN: "hidden",
    CARET_ICON: "caret-icon",
    CARET_RIGHT: "fa-caret-right",
    CARET_DOWN: "fa-caret-down",
    ICON: "fas",
    CONTAINER: "json-container"
} as const;

const STYLE_ID = "jsonview-styles";

// ─── Embedded Styles ──────────────────────────────────────────────────────────

/**
 * Self-contained CSS with pure CSS triangle icons — no external font dependencies.
 * The .fas / .fa-caret-down / .fa-caret-right classes use CSS border tricks to
 * render directional triangles without FontAwesome or any icon font.
 */
const EMBEDDED_CSS = `
.json-container {
  font-family: system-ui, Avenir, Helvetica, Arial, sans-serif;
  font-size: 16px;
  background-color: #fff;
  color: #808080;
  box-sizing: border-box;
}

.json-container .line {
  margin: 4px 0;
  display: flex;
  justify-content: flex-start;
}

.json-container .caret-icon {
  width: 18px;
  text-align: center;
  cursor: pointer;
}

.json-container .empty-icon {
  width: 18px;
}

.json-container .json-type {
  margin-right: 4px;
  margin-left: 4px;
}

.json-container .json-key {
  color: #444;
  margin-right: 4px;
  margin-left: 4px;
  min-width: 12px;
}

.json-container .json-index {
  margin-right: 4px;
  margin-left: 4px;
}

.json-container .json-value {
  margin-left: 4px;
}

.json-container .json-number {
  color: #f9ae58;
}

.json-container .json-boolean {
  color: #ec5f66;
}

.json-container .json-string {
  color: #86b25c;
}

.json-container .hidden {
  display: none;
}

/* CSS-only triangle icons — right-pointing and down-pointing carets */
.json-container .fas {
  display: inline-block;
  border-style: solid;
  width: 0;
  height: 0;
}

.json-container .fa-caret-down {
  border-width: 6px 5px 0 5px;
  border-color: #808080 transparent;
}

.json-container .fa-caret-right {
  border-width: 5px 0 5px 6px;
  border-color: transparent transparent transparent #808080;
}
`;

// ─── Style Injection ──────────────────────────────────────────────────────────

function injectStyles(): void {
    if (document.getElementById(STYLE_ID)) {
        return;
    }
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = EMBEDDED_CSS;
    document.head.appendChild(style);
}

// ─── DOM Utilities ────────────────────────────────────────────────────────────

function listen(node: Element, event: string, handler: EventListener): () => void {
    node.addEventListener(event, handler);
    return () => node.removeEventListener(event, handler);
}

function detach(node: Element): void {
    node.parentNode?.removeChild(node);
}

// ─── Data Type Utility ────────────────────────────────────────────────────────

function getDataType(val: unknown): JsonDataType {
    if (Array.isArray(val)) {
        return "array";
    }
    if (val === null) {
        return "null";
    }
    return typeof val as JsonDataType;
}

// ─── Templates ────────────────────────────────────────────────────────────────

function expandedTemplate({ key, size, isExpanded }: ExpandedTemplateParams): string {
    const caretClass = isExpanded ? CSS_CLASS.CARET_DOWN : CSS_CLASS.CARET_RIGHT;
    return `
    <div class="line">
      <div class="caret-icon"><i class="fas ${ caretClass }"></i></div>
      <div class="json-key">${ htmlEscape(String(key ?? "")) }</div>
      <div class="json-size">${ htmlEscape(String(size ?? "")) }</div>
    </div>
  `;
}

function notExpandedTemplate({ key, value, type }: NotExpandedTemplateParams): string {
    return `
    <div class="line">
      <div class="empty-icon"></div>
      <div class="json-key">${ htmlEscape(String(key ?? "")) }</div>
      <div class="json-separator">:</div>
      <div class="json-value ${ type }">${ htmlEscape(value) }</div>
    </div>
  `;
}

// ─── Node Visibility ──────────────────────────────────────────────────────────

function hideNodeChildren(node: JsonNode): void {
    node.children.forEach((child) => {
        child.el?.classList.add(CSS_CLASS.HIDDEN);
        if (child.isExpanded) {
            hideNodeChildren(child);
        }
    });
}

function showNodeChildren(node: JsonNode): void {
    node.children.forEach((child) => {
        child.el?.classList.remove(CSS_CLASS.HIDDEN);
        if (child.isExpanded) {
            showNodeChildren(child);
        }
    });
}

function setCaretIconDown(node: JsonNode): void {
    if (node.children.length > 0 && node.el) {
        node.el
            .querySelector(`.${ CSS_CLASS.ICON }`)
            ?.classList.replace(CSS_CLASS.CARET_RIGHT, CSS_CLASS.CARET_DOWN);
    }
}

function setCaretIconRight(node: JsonNode): void {
    if (node.children.length > 0 && node.el) {
        node.el
            .querySelector(`.${ CSS_CLASS.ICON }`)
            ?.classList.replace(CSS_CLASS.CARET_DOWN, CSS_CLASS.CARET_RIGHT);
    }
}

// ─── Node Element Construction ────────────────────────────────────────────────

function getSizeString(node: JsonNode): string | null {
    const len = node.children.length;
    if (node.type === "array") {
        return `[${ len }]`;
    }
    if (node.type === "object") {
        return `{${ len }}`;
    }
    return null;
}

function getValueString(node: JsonNode): string {
    switch (node.type) {
        case "string":
            return node.value ? String(node.value) : "\"\"";
        case "number":
        case "boolean":
        case "null":
            return String(node.value);
        case "array":
            return `[${ node.children.length }]`;
        case "object":
            return `{${ node.children.length }}`;
        default:
            return String(node.value);
    }
}

function getTypeClass(node: JsonNode): string {
    switch (node.type) {
        case "string":
        case "number":
        case "boolean":
        case "null":
        case "array":
        case "object":
            return `json-${ node.type }`;
        default:
            return `json-${ typeof node.value }`;
    }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Toggle the expand/collapse state of a node
 */
export function toggleNode(node: JsonNode): void {
    if (node.isExpanded) {
        node.isExpanded = false;
        setCaretIconRight(node);
        hideNodeChildren(node);
    } else {
        node.isExpanded = true;
        setCaretIconDown(node);
        showNodeChildren(node);
    }
}

// ─── Node Element Construction (continued) ────────────────────────────────────

function createNodeElement(node: JsonNode): HTMLElement {
    const wrapper = document.createElement("div");

    if (node.children.length > 0) {
        wrapper.innerHTML = expandedTemplate({
            key: node.key,
            size: getSizeString(node),
            isExpanded: node.isExpanded
        });
        const caretEl = wrapper.querySelector(`.${ CSS_CLASS.CARET_ICON }`);
        if (caretEl) {
            node.dispose = listen(caretEl, "click", () => toggleNode(node));
        }
    } else {
        wrapper.innerHTML = notExpandedTemplate({
            key: node.key,
            value: getValueString(node),
            type: getTypeClass(node)
        });
    }

    const lineEl = wrapper.children[0] as HTMLElement;

    if (node.parent !== null) {
        lineEl.classList.toggle(CSS_CLASS.HIDDEN, !node.isExpanded);
    }

    lineEl.style.marginLeft = `${ node.depth * 18 }px`;

    return lineEl;
}

// ─── Node Factory ─────────────────────────────────────────────────────────────

function createNode(opt: CreateNodeOptions = {}): JsonNode {
    const isEmptyObject = (val: JsonValue): boolean =>
        getDataType(val) === "object" && Object.keys(val as JsonObject).length === 0;

    let value: JsonValue = Object.prototype.hasOwnProperty.call(opt, "value")
        ? (opt.value ?? null)
        : null;

    if (isEmptyObject(value)) {
        value = "{}";
    }

    return {
        key: opt.key ?? null,
        parent: opt.parent ?? null,
        value,
        isExpanded: opt.isExpanded ?? false,
        type: opt.type ?? null,
        children: opt.children ?? [],
        el: opt.el ?? null,
        depth: opt.depth ?? 0,
        dispose: null
    };
}

function createSubnode(data: JsonValue, node: JsonNode): void {
    if (typeof data === "object" && data !== null) {
        for (const key in data) {
            const childValue = (data as JsonObject)[key];
            const child = createNode({
                value: childValue,
                key,
                depth: node.depth + 1,
                type: getDataType(childValue),
                parent: node
            });
            node.children.push(child);
            createSubnode(childValue, child);
        }
    }
}

function parseJson(data: string | JsonValue): JsonValue {
    return typeof data === "string" ? (JSON.parse(data) as JsonValue) : data;
}

/**
 * Recursively traverse all nodes in the tree, invoking callback on each
 */
export function traverse(node: JsonNode, callback: (node: JsonNode) => void): void {
    callback(node);
    node.children.forEach((child) => traverse(child, callback));
}

/**
 * Build a JsonNode tree from a JSON string or parsed object
 */
export function create(jsonData: string | JsonValue): JsonNode {
    const parsed = parseJson(jsonData);
    const rootNode = createNode({
        value: parsed,
        key: getDataType(parsed),
        type: getDataType(parsed)
    });
    createSubnode(parsed, rootNode);
    return rootNode;
}

/**
 * Render a pre-built JsonNode tree into a target DOM element
 */
export function render(tree: JsonNode, targetElement: HTMLElement): void {
    injectStyles();
    const container = document.createElement("div");
    container.className = CSS_CLASS.CONTAINER;
    traverse(tree, (node) => {
        node.el = createNodeElement(node);
        container.appendChild(node.el);
    });
    targetElement.appendChild(container);
}

/**
 * Parse JSON data and render it directly into a target DOM element
 * @returns The root JsonNode of the rendered tree
 */
export function renderJSON(jsonData: string | JsonValue, targetElement: HTMLElement): JsonNode {
    const tree = create(parseJson(jsonData));
    render(tree, targetElement);
    return tree;
}

/**
 * Expand all nodes in the subtree rooted at node
 */
export function expand(node: JsonNode): void {
    traverse(node, (child) => {
        child.el?.classList.remove(CSS_CLASS.HIDDEN);
        child.isExpanded = true;
        setCaretIconDown(child);
    });
}

/**
 * Collapse all child nodes below node
 */
export function collapse(node: JsonNode): void {
    traverse(node, (child) => {
        child.isExpanded = false;
        if (child.depth > node.depth) {
            child.el?.classList.add(CSS_CLASS.HIDDEN);
        }
        setCaretIconRight(child);
    });
}

/**
 * Destroy the tree, removing all event listeners and the container element from the DOM
 */
export function destroy(tree: JsonNode): void {
    traverse(tree, (node) => node.dispose?.());
    if (tree.el?.parentNode) {
        detach(tree.el.parentNode as Element);
    }
}

/**
 * Default export — full public interface
 */
export default {
    toggleNode,
    render,
    create,
    renderJSON,
    expand,
    collapse,
    traverse,
    destroy
};
